import { Box, Typography } from "@analyticsMD/quil";
import ErrorIcon from "@mui/icons-material/Error";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { css } from "@emotion/css";
import { common, grey } from "@mui/material/colors";
import {
  CARD_BORDER,
  DECLINE_RED,
  PURPLE_TINT,
  QVENTUS_PURPLE_EDGE,
} from "./malnutrition-colors";
import CollapsibleSection from "../shared/collapsible-section";

import type { FC } from "react";
import type {
  MalnutritionCriterion,
  MalnutritionCriterionMetric,
  MalnutritionCriterionMetricRow,
  MalnutritionCriterionQuote,
  MalnutritionStatus,
} from "../../types/Malnutrition";

type Variant = "flagged" | "protocol";

interface VariantTheme {
  headerLabel: string;
  summaryLabel: (n: number) => string;
  Icon: typeof ErrorIcon;
}

// Headings differ by icon and wording only — never by colour (ENG-30844). Red
// used to mark "why flagged" and blue "protocol trigger", which made the
// section header compete with the patient data underneath it for the same
// severity vocabulary.
const VARIANTS: Record<Variant, VariantTheme> = {
  flagged: {
    headerLabel: "WHY FLAGGED",
    summaryLabel: (n) => `· ${n} criteria met`,
    Icon: ErrorIcon,
  },
  protocol: {
    headerLabel: "PROTOCOL TRIGGER",
    summaryLabel: (n) => `· ${n} ${n > 1 ? "rules" : "rule"} met`,
    Icon: AssignmentIcon,
  },
};

// The serializer labels the percent-change row "Change" (see
// _malnutrition_metric_rows in backend/api/serializers.py), formatted as a
// signed percentage.
const DELTA_ROW_LABEL = "Change";

// Red means decline, so the sign has to agree — the label alone isn't enough.
// The serializer emits a Change row for any non-null percent_change, which
// comes straight through from EBA's proto and is not constrained to negatives;
// a weight *gain* is not clinical decline and stays neutral.
const isDeclineRow = (row: MalnutritionCriterionMetricRow): boolean =>
  row.label === DELTA_ROW_LABEL && row.value.trim().startsWith("-");

const styles = {
  card: css`
    background: ${common.white};
    border: 1px solid ${CARD_BORDER};
    border-radius: 0.375rem;
    padding: 0.75rem 0.875rem;
    margin-bottom: 0.625rem;
  `,
  quoteBlock: css`
    background: ${PURPLE_TINT};
    border-left: 3px solid ${QVENTUS_PURPLE_EDGE};
    padding: 0.625rem 0.75rem;
    margin: 0.5rem 0 0.625rem 0;
    font-size: 0.875rem;
    color: ${grey[800]};
    border-radius: 0.125rem;
  `,
  source: css`
    color: ${grey[700]};
    font-size: 0.75rem;
  `,
  metricRow: css`
    display: flex;
    gap: 0.375rem;
    font-size: 0.85rem;
    line-height: 1.45;
  `,
};

const cardSxStyles = {
  title: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: grey[800],
    marginBottom: "0.375rem",
  },
};

const metricSx = (isDelta: boolean) => ({
  label: {
    fontSize: "0.85rem",
    color: isDelta ? DECLINE_RED : grey[700],
    fontWeight: 600,
  },
  value: {
    fontSize: "0.85rem",
    color: isDelta ? DECLINE_RED : grey[800],
    fontWeight: isDelta ? 600 : 400,
  },
});

const headingSx = {
  fontSize: "0.8125rem",
  fontWeight: 700,
  color: grey[700],
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  display: "flex",
  alignItems: "center",
};

const iconSx = {
  backgroundColor: grey[800],
  color: common.white,
  borderRadius: "0.25rem",
  padding: "0.125rem",
  fontSize: "1rem",
  marginRight: "0.5rem",
};

const MetricCriterionCard: FC<{
  criterion: MalnutritionCriterionMetric;
}> = ({ criterion }) => (
  <Box className={styles.card}>
    <Typography sx={cardSxStyles.title}>{criterion.title}</Typography>
    {criterion.rows.map((row, idx) => {
      const sx = metricSx(isDeclineRow(row));
      return (
        <Box key={idx} className={styles.metricRow}>
          {row.label && (
            <Box component="span" sx={sx.label}>
              {row.label}:
            </Box>
          )}
          <Box component="span" sx={sx.value}>
            {row.value}
          </Box>
        </Box>
      );
    })}
  </Box>
);

const QuoteCriterionCard: FC<{
  criterion: MalnutritionCriterionQuote;
}> = ({ criterion }) => (
  <Box className={styles.card}>
    <Typography sx={cardSxStyles.title}>{criterion.title}</Typography>
    <Box className={styles.quoteBlock}>&ldquo;{criterion.quote}&rdquo;</Box>
    <Box className={styles.source}>
      {[
        criterion.source_kind,
        criterion.source_author,
        criterion.source_timestamp,
      ]
        .filter(Boolean)
        .join(" · ")}
    </Box>
  </Box>
);

export const CriterionCard: FC<{
  criterion: MalnutritionCriterion;
}> = ({ criterion }) => {
  if (criterion.kind === "metric") {
    return <MetricCriterionCard criterion={criterion} />;
  }
  return <QuoteCriterionCard criterion={criterion} />;
};

const CriteriaSection: FC<{
  variant: Variant;
  criteria: MalnutritionCriterion[];
}> = ({ variant, criteria }) => {
  const { headerLabel, summaryLabel, Icon } = VARIANTS[variant];
  return (
    <CollapsibleSection
      defaultOpen
      title={headerLabel}
      icon={<Icon sx={iconSx} />}
      summary={<>{summaryLabel(criteria.length)}</>}
      headingTextSx={headingSx}
      outerClassName={css`
        background: ${grey[50]};
        border-radius: 0.5rem;
        margin: 0 0.75rem 0.75rem;
        padding: 0.25rem 1rem 0.25rem 1rem;
      `}
      dataTestId={
        variant === "protocol"
          ? "protocol-trigger-section"
          : "why-flagged-section"
      }
    >
      {criteria.map((criterion, idx) => (
        <CriterionCard key={idx} criterion={criterion} />
      ))}
    </CollapsibleSection>
  );
};

interface WhyFlaggedSectionProps {
  criteria: MalnutritionCriterion[];
  status: MalnutritionStatus;
}

// The criteria this section renders under "WHY FLAGGED", in render order — the
// single source of truth for that set. The criteria-feedback panel checklist has
// to match it exactly (same items, same labels, same order, same count as the
// "· N criteria met" summary), so it consumes this rather than re-filtering; a
// second filter would drift. A blank status renders no section at all, hence [].
export const whyFlaggedCriteria = (
  criteria: MalnutritionCriterion[],
  status: MalnutritionStatus | null
): MalnutritionCriterion[] =>
  status === null || status === "protocol"
    ? []
    : criteria.filter((c) => c.source === "model");

// Criteria split by source: model -> "Why Flagged", protocol -> "Protocol
// Trigger", each shown when present. Exception: a "protocol" status shows only
// the Protocol Trigger section (Why Flagged is suppressed even if there are
// model criteria). (A blank status hides everything — handled by the caller.)
const WhyFlaggedSection: FC<WhyFlaggedSectionProps> = ({
  criteria,
  status,
}) => {
  const modelCriteria = whyFlaggedCriteria(criteria, status);
  const protocolCriteria = criteria.filter((c) => c.source === "protocol");
  const showModel = modelCriteria.length > 0;
  return (
    <>
      {showModel && (
        <CriteriaSection variant="flagged" criteria={modelCriteria} />
      )}
      {protocolCriteria.length > 0 && (
        <CriteriaSection variant="protocol" criteria={protocolCriteria} />
      )}
    </>
  );
};

export default WhyFlaggedSection;
